"use client";

import { ProductData } from "@/types/product";
import { useState } from "react";

interface ProductInformationTabsProps {
  product: ProductData;
}

export default function ProductInformationTabs({
  product,
}: ProductInformationTabsProps) {
  const [activeTab, setActiveTab] = useState<"description" | "faq">(
    "description"
  );

  const renderDescriptionContent = () => {
    return (
      <div className="space-y-8 sm:space-y-10">
        {/* Main Description */}
        <div>
          <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 sm:mb-5">
            About {product.productName}
          </h3>
          <div className="prose prose-base sm:prose-lg max-w-none">
            <div
              className="text-sm sm:text-base text-gray-700 leading-relaxed mb-4 sm:mb-6 rich-text"
              dangerouslySetInnerHTML={{
                __html:
                  product.productInformation.description ||
                  product.productBase.about ||
                  "This clinically-proven weight management medication is designed to support your weight loss journey under professional medical supervision.",
              }}
            />
          </div>
        </div>

        {/* How Medication Works */}
        {product.productHowMedicationWorks.content && (
          <div className="pb-6 sm:pb-8 border-t border-gray-200 pt-6 sm:pt-8">
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-5">
              How this medication works
            </h3>
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-lg px-4 sm:px-6 py-4 sm:py-5 mb-6 sm:mb-8">
              <div
                className="text-sm sm:text-base text-gray-700 leading-relaxed rich-text"
                dangerouslySetInnerHTML={{
                  __html: product.productHowMedicationWorks.content,
                }}
              />
            </div>
            {product.productHowMedicationWorks.steps &&
              product.productHowMedicationWorks.steps.length > 0 && (
                <div className="space-y-4">
                  {product.productHowMedicationWorks.steps.map(
                    (step, index) => (
                      <div
                        key={index}
                        className="bg-gray-50 border border-gray-200 rounded-lg px-6 py-4"
                      >
                        <h4 className="text-lg font-bold text-gray-900 mb-2">
                          {step.label}
                        </h4>
                        <div
                          className="text-gray-700 leading-relaxed rich-text"
                          dangerouslySetInnerHTML={{ __html: step.value }}
                        />
                      </div>
                    )
                  )}
                </div>
              )}
          </div>
        )}

        {/* Key Benefits & Features */}
        {((product.productBase.keyBenefits &&
          product.productBase.keyBenefits.length > 0) ||
          (product.productBase.keyFeatures &&
            product.productBase.keyFeatures.length > 0)) && (
          <div className="pb-8 border-t border-gray-200 pt-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-5">
              Key benefits & features
            </h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {/* Combine keyBenefits and keyFeatures */}
              {[
                ...(product.productBase.keyBenefits || []),
                ...(product.productBase.keyFeatures || []),
              ].map((item, idx) => (
                <div
                  key={idx}
                  className="flex items-start gap-3 bg-green-50 border border-green-200 rounded-lg px-5 py-4"
                >
                  <div className="flex-shrink-0 w-6 h-6 bg-green-600 rounded flex items-center justify-center mt-0.5">
                    <svg
                      className="w-4 h-4 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <span className="text-gray-700 leading-relaxed flex-1">
                    {item}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* How to Use */}
        {product.productHowToUse.list &&
          product.productHowToUse.list.length > 0 && (
            <div className="pb-8 border-t border-gray-200 pt-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-5">
                How to use
              </h3>
              <div className="space-y-4">
                {product.productHowToUse.list.map((item, idx) => (
                  <div
                    key={idx}
                    className="bg-gray-50 border border-gray-200 rounded-lg px-6 py-4"
                  >
                    <h4 className="text-lg font-bold text-gray-900 mb-2">
                      {item.label}
                    </h4>
                    <div
                      className="text-gray-700 leading-relaxed rich-text"
                      dangerouslySetInnerHTML={{ __html: item.description }}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

        {/* Important Information */}
        {product.productImportantInfo.content && (
          <div className="bg-amber-50 border-2 border-amber-200 rounded-xl px-6 sm:px-8 py-5 sm:py-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-amber-500 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 sm:w-6 sm:h-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <div className="flex-1">
                <h4 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3">
                  {product.productImportantInfo.label ||
                    "Important information"}
                </h4>
                <div
                  className="text-gray-700 leading-relaxed rich-text"
                  dangerouslySetInnerHTML={{
                    __html: product.productImportantInfo.content,
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderFAQContent = () => {
    // Use ProductFAQs from the product data
    const faqs = product.productFAQs.list || [];

    if (faqs.length === 0) {
      // Show default FAQs if none exist
      const defaultFAQs = [
        {
          question: "How does this weight loss treatment work?",
          answer:
            "Our weight loss treatments work by utilizing clinically proven medications that help regulate appetite and metabolism. Combined with professional medical support and lifestyle guidance, this comprehensive approach supports sustainable weight loss. Your treatment plan is personalised based on your health profile and goals.",
        },
        {
          question: "Is this treatment right for me?",
          answer:
            "Our treatments are suitable for adults with a BMI over 27 who are looking to lose weight under medical supervision. During the consultation process, our healthcare professionals will review your medical history, current health status, and weight loss goals to determine if this treatment is appropriate for you.",
        },
        {
          question: "How long will delivery take?",
          answer:
            "Once your consultation is approved and payment is confirmed, we aim to dispatch your medication within 24 hours. Delivery typically takes 1-3 working days. All packages are sent in discreet, unbranded packaging to protect your privacy.",
        },
        {
          question: "What kind of support will I receive?",
          answer:
            "You'll have access to our qualified medical team throughout your treatment journey. This includes regular check-ins, progress monitoring, dosage adjustments if needed, and guidance on diet and lifestyle. Our support team is available to answer questions and provide assistance whenever you need it.",
        },
        {
          question: "Are there any side effects?",
          answer:
            "As with any medication, there can be side effects. Common ones include mild nausea, digestive changes, or headaches, which typically subside as your body adjusts. Our medical team will discuss potential side effects during your consultation and monitor you throughout treatment. If you experience any concerning symptoms, our team is available to help.",
        },
        {
          question: "What if the treatment doesn't work for me?",
          answer:
            "Weight loss journeys are unique to each individual. If you're not seeing the results you expected, our medical team can adjust your treatment plan, modify dosages, or explore alternative options. We're committed to finding the right solution for your needs and offer a satisfaction guarantee on our services.",
        },
      ];

      return (
        <div className="space-y-4">
          {defaultFAQs.map((item, index) => (
            <details
              key={index}
              className="group bg-white border-2 border-gray-200 rounded-lg hover:border-gray-300 transition-all duration-300"
            >
              <summary className="flex items-center justify-between px-6 py-5 cursor-pointer list-none">
                <div className="flex items-start gap-4 flex-1">
                  <span className="flex-shrink-0 w-8 h-8 bg-[#7771B8] rounded text-white font-bold flex items-center justify-center text-sm">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <h4 className="font-bold text-gray-900 text-lg leading-tight pr-4">
                    {item.question}
                  </h4>
                </div>
                <svg
                  className="w-6 h-6 text-gray-400 flex-shrink-0 transition-transform duration-300 group-open:rotate-180"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </summary>
              <div className="px-6 pb-6 pl-[72px] border-t border-gray-100">
                <p className="text-gray-700 leading-relaxed pt-5">
                  {item.answer}
                </p>
              </div>
            </details>
          ))}

          {/* Additional Help Section */}
          <div className="mt-8 bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl px-6 sm:px-8 py-5 sm:py-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 sm:w-6 sm:h-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z"
                  />
                </svg>
              </div>
              <div className="flex-1">
                <h4 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">
                  Still have questions?
                </h4>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  Our expert support team is here to help. Contact us and
                  we&#39;ll get back to you within 24 hours.
                </p>
                <button className="bg-[#7771B8] hover:bg-[#6860a3] text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-[1.02]">
                  Contact support
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {faqs.map((item, index) => (
          <details
            key={index}
            className="group bg-white border-2 border-gray-200 rounded-lg hover:border-gray-300 transition-all duration-300"
          >
            <summary className="flex items-center justify-between px-4 sm:px-6 py-4 sm:py-5 cursor-pointer list-none">
              <div className="flex items-start gap-3 sm:gap-4 flex-1">
                <span className="flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 bg-[#7771B8] rounded text-white font-bold flex items-center justify-center text-xs sm:text-sm">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <h4 className="font-bold text-gray-900 text-base sm:text-lg leading-tight pr-3 sm:pr-4">
                  {item.question}
                </h4>
              </div>
              <svg
                className="w-5 h-5 sm:w-6 sm:h-6 text-gray-400 flex-shrink-0 transition-transform duration-300 group-open:rotate-180"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </summary>
            <div className="px-4 sm:px-6 pb-4 sm:pb-6 pl-[52px] sm:pl-[72px] border-t border-gray-100">
              <div
                className="text-sm sm:text-base text-gray-700 leading-relaxed pt-4 sm:pt-5 rich-text"
                dangerouslySetInnerHTML={{ __html: item.answer }}
              />
            </div>
          </details>
        ))}
      </div>
    );
  };

  const renderContentNavigation = () => {
    // Content navigation is not part of ProductData interface
    // Return null to hide the navigation sidebar
    return null;
  };

  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-20 2xl:px-8">
        {/* Section Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-5">
            <div className="h-1 w-12 bg-gradient-to-r from-[#7771B8] to-[#6B5B9A] rounded-full" />
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900">
              Product information
            </h2>
          </div>
          <p className="text-sm sm:text-base text-gray-600 max-w-3xl">
            Everything you need to know about this product, from detailed
            descriptions to customer reviews.
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-wrap gap-2 bg-white border-2 border-gray-200 rounded-lg p-2 shadow-lg">
            {[
              {
                key: "description",
                label: "Product information",
                icon: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
              },
              {
                key: "faq",
                label: "FAQ",
                icon: "M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
              },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as "description" | "faq")}
                className={`group flex items-center gap-3 px-6 py-3.5 rounded-lg font-semibold text-sm transition-all duration-300 ${
                  activeTab === tab.key
                    ? "bg-gradient-to-r from-[#7771B8] to-[#6B5B9A] text-white shadow-lg"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                }`}
              >
                <svg
                  className={`w-5 h-5 transition-transform duration-300 ${
                    activeTab === tab.key
                      ? "scale-110"
                      : "group-hover:scale-110"
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d={tab.icon}
                  />
                </svg>
                <span>{tab.label}</span>
                {activeTab === tab.key && (
                  <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-10">
          {/* Main Content */}
          <div className="w-full">
            <div className="bg-white border-2 border-gray-200 rounded-xl px-6 sm:px-8 py-8 sm:py-10 shadow-lg">
              {activeTab === "description" && renderDescriptionContent()}
              {activeTab === "faq" && renderFAQContent()}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
